import { ChakraProvider, extendTheme, Drawer, ThemeConfig, Box, Text, Flex, Spinner } from '@chakra-ui/react';
import { cssVar } from '@chakra-ui/theme-tools';
import { Api } from 'api';
import {
  ArcElement,
  Legend,
  Chart as ChartJS,
  Tooltip,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
} from 'chart.js';
import { localStorageUserAuthInfoKey, updateLocalStorageAuthInfo, setPageTitle } from 'core';
import { hideLoading, setAuthUserInfo, setIsAuthInitDone, showLoading } from 'features';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { RootState } from 'store';
import { AuthUserInfo } from 'types';
import './styles/main.css';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { i18n } from '@lingui/core';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title);

const themeConfig: ThemeConfig = {
  // @ts-ignore
  initialColorMode: 'system',
  useSystemColorMode: true,
};

// @ts-ignore
Drawer.defaultProps = {
  // @ts-ignore
  ...(Drawer.defaultProps || {}),
  placement: 'left',
};

const customTheme = extendTheme({
  config: themeConfig,
  components: {
    Drawer: {
      variants: {
        permanent: {
          dialog: {
            transform: 'none !important',
            boxShadow: 'none !important',
            pointerEvents: 'auto',
            zIndex: cssVar('chakra-zIndices-base').reference,
            width: '280px !important',
          },
          dialogContainer: {
            pointerEvents: 'none',
            width: '280px !important',
            zIndex: 1,
          },
        },
      },
      defaultProps: {
        placement: 'left',
      },
    },
  },
});

export const App = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: RootState) => state.core.isLoading);
  const isAuthInitDone = useSelector((state: RootState) => state.auth.isAuthInitDone);

  useEffect(() => {
    const authUserInfoStr = localStorage.getItem(localStorageUserAuthInfoKey);

    dispatch(showLoading());

    if (authUserInfoStr) {
      const authUserInfo: AuthUserInfo = JSON.parse(authUserInfoStr);
      if (authUserInfo.token) {
        dispatch(setAuthUserInfo(authUserInfo));
        Api.token = authUserInfo.token;

        if (authUserInfo.expertisePoint?.countryId === 2) {
          i18n.activate('ru');
          Api.language = 'ru';
        }
      }
    }
    dispatch(setIsAuthInitDone(true));
    dispatch(hideLoading());
  }, []);

  // Set initial page title based on domain
  useEffect(() => {
    setPageTitle();
  }, []);

  useEffect(() => {
    if (process.env.PUBLIC_URL?.includes('localhost')) {
      return;
    }
    if (process.env.REACT_APP_BASE_URL?.includes('localhost')) {
      return;
    }

    Sentry.init({
      dsn: 'https://4562468ffa49111c9610111f13a31989@o1070208.ingest.us.sentry.io/4509088911654913',
    });

    if (process.env.REACT_APP_POSTHOG_KEY) {
      posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
        api_host: process.env.REACT_APP_POSTHOG_HOST,
      });
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const authUserInfoStrCheck = localStorage.getItem(localStorageUserAuthInfoKey);
      if (authUserInfoStrCheck) {
        Api.User.tokenRefresh().then((response) => {
          const authUserInfoStr = localStorage.getItem(localStorageUserAuthInfoKey);
          if (authUserInfoStr) {
            const authUserInfo = JSON.parse(authUserInfoStr);
            authUserInfo.token = response.data;
            updateLocalStorageAuthInfo(authUserInfo);
            Api.token = authUserInfo.token;
            dispatch(setAuthUserInfo(authUserInfo));
          }
        });
      }
    }, 10 * 60 * 1000); // 10 dakikada bir

    return () => clearInterval(interval);
  }, []);

  return (
    <ChakraProvider theme={customTheme}>
      {isLoading && (
        <Box
          position="fixed"
          top="0"
          bottom="0"
          left="0"
          right="0"
          width="100vw"
          height="100vh"
          zIndex="99999"
          sx={{
            backgroundColor: 'rgba(0.2, 0.2, 0.2, 0.4)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
            <Spinner size="md" mr={2} /> <Text>Yükleniyor...</Text>
          </Flex>
        </Box>
      )}
      {isAuthInitDone && (
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      )}
    </ChakraProvider>
  );
};
